import React, { Fragment, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, Link, useParams } from 'react-router-dom';
import Navbar from '../HomeScreen/Navbar';
import Footer from '../HomeScreen/bottomsection/Footer';
import swal from 'sweetalert';
import MetaTag from '../../layout/MetaTag';
import Loader from '../../layout/loader';
import { returnOrderByUserAction, clearErrorAction } from '../../redux/actions/orderAction';

import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import '../../css/returnOrders.css';

function ReturnOrderByUser(props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const routeParams = useParams();

  const orderId = routeParams?.orderId || props?.match?.params?.orderId;

  // Redux state
  const { loading, returnOrder, error } = useSelector((state) => state.returnOrder);
  const { user } = useSelector((state) => state.user);

  const [returnReason, setReturnReason] = useState('');
  const [returnType, setReturnType] = useState('Return');
  const [isChacked, setIsChacked] = useState(false);
  const [accept, setAccept] = useState(false);
  const [images, setImages] = useState([]);

  useEffect(() => {
    if (error) {
      swal({
        title: 'Error',
        text: error,
        icon: 'error',
      });
      dispatch(clearErrorAction());
    }
    if (returnOrder) {
      swal({
        title: 'Return Request Submitted!',
        text: 'Your return/exchange request has been initiated successfully. We will notify you once pickup is scheduled.',
        icon: 'success',
      });
      history.push('/user/All/return/orders');
    }
  }, [dispatch, error, history, returnOrder]);

  const returnSubmitHandler = (e) => {
    e.preventDefault();

    if (!isChacked) {
      return swal({
        title: 'Confirmation Needed',
        text: 'Please confirm that you wish to return/exchange this order item.',
        icon: 'info',
      });
    }

    if (!accept) {
      return swal({
        title: 'Terms & Return Policy',
        text: 'Please accept the IM Online return policy to proceed.',
        icon: 'info',
      });
    }

    if (!returnReason.trim()) {
      return swal({
        title: 'Reason Required',
        text: 'Please describe the issue or reason for your return in detail.',
        icon: 'info',
      });
    }

    const formData = new FormData();
    formData.append('name', user?.firstName || 'Customer');
    formData.append('email', user?.email || '');
    formData.append('phoneNo', user?.number || '');
    formData.append('returnOrderName', user?.firstName || 'Customer');
    formData.append('returnOrderSize', user?.firstName || '');
    formData.append('ReturnOrderColor', user?.firstName || '');
    formData.append('returnType', returnType);
    formData.append('returnReason', returnReason);
    formData.append('orderId', orderId);

    if (images && images.length > 0) {
      for (let index = 0; index < images.length; index++) {
        formData.append('image', images[index]);
      }
    }

    dispatch(returnOrderByUserAction(formData));
  };

  const handleImageChange = (e) => {
    if (e.target.files) {
      setImages(Array.from(e.target.files));
    }
  };

  return (
    <Fragment>
      <MetaTag
        title={`Request Return & Exchange | IM Online Shopping`}
        description="Initiate a return or exchange for your delivered order with doorstep pickup on IM Online Shopping."
      />

      <div className="ret-page-wrapper">
        <Navbar />

        <main className="ret-container">
          {/* Breadcrumb */}
          <nav className="ret-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="ret-breadcrumb-sep">/</span>
            <Link to="/userprofile">My Account</Link>
            <span className="ret-breadcrumb-sep">/</span>
            <Link to="/user/All/return/orders">Returns & Exchange</Link>
            <span className="ret-breadcrumb-sep">/</span>
            <span className="ret-breadcrumb-current">New Request</span>
          </nav>

          {/* Hero Header */}
          <section className="ret-hero">
            <div className="ret-hero-left">
              <div className="ret-hero-icon-box">
                <AssignmentReturnOutlinedIcon />
              </div>
              <div className="ret-hero-text">
                <h1>Request Return or Exchange</h1>
                <p>
                  Initiate doorstep pickup and refund/exchange for Order{' '}
                  <strong style={{ color: '#111315' }}>#{String(orderId).slice(-8).toUpperCase()}</strong>
                </p>
                <div className="ret-policy-chips">
                  <span className="ret-chip">
                    <VerifiedUserOutlinedIcon /> Free Doorstep Pickup
                  </span>
                  <span className="ret-chip">Tags & Packaging Intact</span>
                </div>
              </div>
            </div>

            <div className="ret-hero-actions">
              <Link to="/user/All/return/orders" className="ret-btn-back">
                <ArrowBackIosNewOutlinedIcon style={{ fontSize: '12px' }} />
                <span>View All Return Requests</span>
              </Link>
            </div>
          </section>

          {loading ? (
            <Loader />
          ) : (
            <div className="ret-form-card">
              <form onSubmit={returnSubmitHandler}>
                {/* Step 1: Confirmation Checkboxes */}
                <div className="ret-form-group">
                  <span className="ret-form-label">Step 1: Agreement & Policy Confirmation</span>

                  <label className="ret-checkbox-row">
                    <input
                      type="checkbox"
                      checked={isChacked}
                      onChange={(e) => setIsChacked(e.target.checked)}
                      required
                    />
                    <span className="ret-checkbox-text">
                      I confirm that I want to return or exchange this item from Order #{String(orderId).slice(-8).toUpperCase()}
                    </span>
                  </label>

                  <label className="ret-checkbox-row">
                    <input
                      type="checkbox"
                      checked={accept}
                      onChange={(e) => setAccept(e.target.checked)}
                      required
                    />
                    <span className="ret-checkbox-text">
                      I have read and agree to IM Online's{' '}
                      <Link to="/about/return-and-exchange" target="_blank">
                        7-Day Return & Replacement Policy
                      </Link>
                      . I confirm that item tags and original packaging are retained.
                    </span>
                  </label>
                </div>

                {/* Step 2: Return or Exchange Type */}
                <div className="ret-form-group">
                  <label htmlFor="ret-type-select" className="ret-form-label">
                    Step 2: What would you like to do?
                  </label>
                  <select
                    id="ret-type-select"
                    className="ret-select-field"
                    value={returnType}
                    onChange={(e) => setReturnType(e.target.value)}
                  >
                    <option value="Return">Return Item (Direct Refund to Bank / Original Payment)</option>
                    <option value="Exchange">Exchange Item (Different Size or Replacement)</option>
                  </select>
                </div>

                {/* Step 3: Detailed Reason */}
                <div className="ret-form-group">
                  <label htmlFor="ret-reason-text" className="ret-form-label">
                    Step 3: Reason for Return / Exchange
                  </label>
                  <textarea
                    id="ret-reason-text"
                    className="ret-textarea-field"
                    placeholder="Describe the issue in detail (e.g. Size mis-match, damaged packaging, wrong color delivered, item defective, etc.)..."
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    required
                  />
                </div>

                {/* Step 4: Photo Evidence Upload */}
                <div className="ret-form-group">
                  <span className="ret-form-label">Step 4: Upload Photos of Product / Tag (Optional)</span>
                  <label className="ret-file-upload-box" htmlFor="ret-file-input">
                    <CloudUploadOutlinedIcon style={{ fontSize: '32px', color: '#9ca3af', marginBottom: '6px' }} />
                    <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#111315' }}>
                      {images.length > 0
                        ? `${images.length} photo(s) selected: ${images.map((f) => f.name).join(', ')}`
                        : 'Click to upload photos (product label, tag, or damage)'}
                    </div>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      Supported formats: JPG, PNG, WEBP (Max 5MB each)
                    </span>
                    <input
                      id="ret-file-input"
                      type="file"
                      name="image"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                {/* Submit Action */}
                <button type="submit" className="ret-submit-btn" disabled={loading}>
                  <AssignmentReturnOutlinedIcon fontSize="small" />
                  <span>{loading ? 'Submitting Request...' : 'Submit Return & Exchange Request'}</span>
                </button>
              </form>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </Fragment>
  );
}

export default ReturnOrderByUser;